import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { LIST_USERS, INVITE_USER, UPDATE_USER } from '../graphql/operations';
import useApi from './useApi';

// Hook for listing users
export function useListUsers() {
  const { data, loading, error, refetch } = useQuery(LIST_USERS);

  return {
    items: data?.listUsers?.items || [],
    loading,
    error,
    refetch,
  };
}

// Hook for inviting user
export function useInviteUser() {
  const [inviteUser, { loading, error }] = useMutation(INVITE_USER, {
    refetchQueries: [{ query: LIST_USERS }],
  });

  const execute = async (input) => {
    const { data} = await inviteUser({
      variables: { input },
    });
    return data.inviteUser;
  };

  return { execute, loading, error };
}

// Hook for updating user
export function useUpdateUser() {
  const [updateUser, { loading, error }] = useMutation(UPDATE_USER, {
    refetchQueries: [{ query: LIST_USERS }],
  });

  const execute = async (userId, input) => {
    const { data } = await updateUser({
      variables: { userId, input },
    });
    return data.updateUser;
  };

  return { execute, loading, error };
}

// Legacy default export with REST API fallback for backward compatibility
export default function useUsersRest() {
  const api = useApi();

  const listUsers = useCallback(() => api.get('/users'), [api]);
  const inviteUser = useCallback((payload) => api.post('/users/invite', payload), [api]);
  const updateUser = useCallback((userId, payload) => api.put(`/users/${userId}`, payload), [api]);

  return useMemo(
    () => ({
      listUsers,
      inviteUser,
      updateUser,
    }),
    [listUsers, inviteUser, updateUser],
  );
}
